import { useState, useEffect, useRef } from 'react'
import {
  Video,
  Calendar,
  Clock,
  Play,
  Pause,
  Download,
  Search,
  ChevronDown,
  ChevronUp,
  User,
  Mail,
  Globe,
  Briefcase,
  Languages,
  FileText,
  Filter,
} from 'lucide-react'
import { interviewerApi } from '../../lib/api/interviewer'
import { formatDateNumericInTz } from '../../lib/utils/timezone'
import { useInterviewerTimezone } from '../../hooks/useInterviewerTimezone'
import { parseVtt, type VttCue } from '../../lib/utils/parseVtt'
import { buildRecordingSrc } from '../../lib/utils/streamUrl'

interface RecordingDetail {
  id: number
  session_id: number | null
  booking_id: number | null
  recording_url: string | null
  transcript_url: string | null
  recording_password: string | null
  duration_seconds: number | null
  file_size_bytes: number | null
  status: string
  created_at: string
  client_name: string | null
  client_email: string | null
  session_title: string | null
  scheduled_at: string | null
  target_role: string | null
  client_timezone: string | null
  client_language: string | null
}

type TranscriptLine = VttCue & { timestamp: string }

function parseVttExcerpts(vttText: string): TranscriptLine[] {
  return parseVtt(vttText).map((c) => ({ ...c, timestamp: c.time }))
}

function formatDate(dateStr: string | null, tz?: string) {
  if (!dateStr) return '—'
  // Interviewer's timezone, matching the dashboard — not the browser's.
  return formatDateNumericInTz(dateStr, tz)
}

function formatDuration(seconds: number | null) {
  if (!seconds) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

function formatTotalDuration(totalSeconds: number) {
  if (!totalSeconds) return '0 min'
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m} min`
}

function getInitial(name: string | null) {
  return (name || 'C').charAt(0).toUpperCase()
}

export default function InterviewerRecordings() {
  const interviewerTz = useInterviewerTimezone()
  const [recordings, setRecordings] = useState<RecordingDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState<'all' | 'with_transcript'>('all')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [playingId, setPlayingId] = useState<number | null>(null)
  const [pendingPlayId, setPendingPlayId] = useState<number | null>(null)
  const [transcripts, setTranscripts] = useState<Record<number, TranscriptLine[]>>({})
  const [videoDurations, setVideoDurations] = useState<Record<number, number>>({})
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    interviewerApi.getRecordings()
      .then((data: RecordingDetail[]) => {
        const list: RecordingDetail[] = data || []
        setRecordings(list)
        // Eagerly load real duration from video metadata for all recordings
        list.forEach((rec) => {
          const src = buildRecordingSrc(rec.recording_url)
          if (!src) return
          const vid = document.createElement('video')
          vid.preload = 'metadata'
          vid.onloadedmetadata = () => {
            if (vid.duration && isFinite(vid.duration)) {
              setVideoDurations((prev) => ({ ...prev, [rec.id]: Math.round(vid.duration) }))
            }
          }
          vid.src = src
        })
      })
      .catch(() => setRecordings([]))
      .finally(() => setLoading(false))
  }, [])

  const filtered = recordings.filter((r) => {
    const matchesSearch =
      (r.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.session_title || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filter === 'all' || (filter === 'with_transcript' && !!r.transcript_url)
    return matchesSearch && matchesFilter
  })

  const withRecordings = recordings.filter((r) => r.status === 'ready' && !!r.recording_url)
  const totalDurationSeconds = withRecordings.reduce((sum, r) => {
    const actual = videoDurations[r.id]
    return sum + (actual ?? r.duration_seconds ?? 0)
  }, 0)
  const withTranscripts = recordings.filter((r) => !!r.transcript_url).length

  // Play video once it's mounted after row expansion
  useEffect(() => {
    if (pendingPlayId === null) return
    if (expandedId !== pendingPlayId) return
    if (!videoRef.current) return
    videoRef.current.play().then(() => {
      setPlayingId(pendingPlayId)
      setPendingPlayId(null)
    }).catch(() => setPendingPlayId(null))
  }, [pendingPlayId, expandedId])

  async function handleExpand(rec: RecordingDetail) {
    const next = expandedId === rec.id ? null : rec.id
    setExpandedId(next)
    setPlayingId(null)
    if (next !== null) {
      if (rec.transcript_url && !transcripts[rec.id]) {
        try {
          const text = await interviewerApi.getRecordingTranscript(rec.id)
          setTranscripts((prev) => ({ ...prev, [rec.id]: parseVttExcerpts(text) }))
        } catch {
          setTranscripts((prev) => ({ ...prev, [rec.id]: [] }))
        }
      }
      // Duration is loaded eagerly on mount via useEffect — no duplicate fetch needed here
    }
  }

  function handlePlay(rec: RecordingDetail, e: React.MouseEvent) {
    e.stopPropagation()
    if (!rec.recording_url) return
    if (playingId === rec.id) {
      videoRef.current?.pause()
      // onPause handler will set playingId to null
    } else {
      if (expandedId !== rec.id) {
        // Expand the row first, then play once video is mounted
        handleExpand(rec)
        setPendingPlayId(rec.id)
      } else {
        // Row already expanded, video is in DOM
        videoRef.current?.play().then(() => setPlayingId(rec.id)).catch(() => {})
      }
    }
  }

  function handleDownload(rec: RecordingDetail, e: React.MouseEvent) {
    e.stopPropagation()
    const src = buildRecordingSrc(rec.recording_url, { download: true })
    if (!src) return
    const a = document.createElement('a')
    a.href = src
    a.download = `recording_${rec.client_name?.replace(/\s+/g, '_') || rec.id}.mp4`
    a.click()
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: 'var(--text-muted)' }}>
        Loading recordings...
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      <style>{`
        .rec-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 24px; }
        .rec-stat-card { background: white; border: 1px solid var(--border, #e2e8f0); border-radius: 12px; padding: 24px; display: flex; align-items: center; gap: 16px; }
        .rec-stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .rec-stat-value { font-size: 28px; font-weight: 700; color: var(--text-dark, #1a202c); line-height: 1; margin-bottom: 4px; }
        .rec-stat-label { font-size: 13px; color: var(--text-muted, #718096); }
        .rec-search-row { display: flex; gap: 10px; align-items: center; margin-bottom: 20px; flex-wrap: wrap; }
        .rec-search-box { flex: 1 1 180px; min-width: 0; display: flex; align-items: center; gap: 10px; background: white; border: 1px solid var(--border, #e2e8f0); border-radius: 8px; padding: 10px 14px; }
        .rec-search-box input { border: none; outline: none; flex: 1; min-width: 0; font-size: 14px; color: var(--text-dark, #1a202c); background: transparent; }
        .rec-filter-btn { display: flex; align-items: center; gap: 6px; padding: 10px 14px; border: 1px solid var(--border, #e2e8f0); border-radius: 8px; background: white; cursor: pointer; font-size: 14px; color: var(--text-dark); white-space: nowrap; flex-shrink: 0; }
        .rec-filter-btn.active { background: var(--primary-navy, #2d3748); color: white; border-color: var(--primary-navy, #2d3748); }

        .rec-row { border: 1px solid var(--border, #e2e8f0); border-radius: 12px; overflow: hidden; background: white; margin-bottom: 12px; }
        .rec-row-header { display: flex; align-items: center; gap: 14px; padding: 16px 18px; cursor: pointer; transition: background 0.15s; }
        .rec-row-header:hover { background: #f8fafc; }
        .rec-avatar { width: 44px; height: 44px; border-radius: 50%; background: var(--primary-navy, #2d3748); color: white; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 600; flex-shrink: 0; }
        .rec-meta { flex: 1; min-width: 0; }
        .rec-meta-title { font-size: 15px; font-weight: 600; color: var(--text-dark, #1a202c); margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .rec-meta-sub { font-size: 13px; color: var(--text-muted, #718096); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .rec-date-col { text-align: right; flex-shrink: 0; }
        .rec-date-item { display: flex; align-items: center; gap: 5px; font-size: 13px; color: var(--text-muted, #718096); justify-content: flex-end; margin-bottom: 2px; white-space: nowrap; }
        .rec-play-btn { width: 38px; height: 38px; border-radius: 50%; background: var(--primary-navy, #2d3748); color: white; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: opacity 0.15s; }
        .rec-play-btn:hover { opacity: 0.85; }
        .rec-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

        .rec-expanded { border-top: 1px solid var(--border, #e2e8f0); padding: 20px; background: #f8fafc; }
        .rec-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
        .rec-detail-card { background: white; border: 1px solid var(--border, #e2e8f0); border-radius: 10px; padding: 20px; }
        .rec-detail-card-title { font-size: 13px; font-weight: 600; color: var(--text-muted, #718096); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 16px; display: flex; align-items: center; gap: 6px; }
        .rec-info-row { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; font-size: 14px; }
        .rec-info-label { font-weight: 600; color: var(--text-dark, #1a202c); min-width: 90px; flex-shrink: 0; }
        .rec-info-value { color: var(--text-muted, #718096); word-break: break-word; }
        .rec-video-area { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 160px; gap: 12px; }
        .rec-video-placeholder { color: var(--text-muted, #718096); display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; }
        .rec-video-filename { font-size: 12px; color: var(--text-muted, #718096); margin-top: 4px; }
        .rec-video-btns { display: flex; gap: 10px; margin-top: 8px; flex-wrap: wrap; justify-content: center; }
        .rec-video-play-btn { display: flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 8px; border: none; cursor: pointer; font-size: 14px; font-weight: 600; transition: opacity 0.15s; }
        .rec-video-play-btn:hover { opacity: 0.85; }
        .rec-transcript { background: white; border: 1px solid var(--border, #e2e8f0); border-radius: 10px; padding: 20px; }
        .rec-transcript-title { font-size: 13px; font-weight: 600; color: var(--text-muted, #718096); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 12px; display: flex; align-items: center; gap: 6px; }
        .rec-transcript-scroll { max-height: 300px; overflow-y: auto; -webkit-overflow-scrolling: touch; }
        .rec-transcript-row { display: grid; grid-template-columns: 60px 110px 1fr; gap: 12px; padding: 10px 0; border-bottom: 1px solid #f1f5f9; align-items: start; }
        .rec-transcript-row:last-child { border-bottom: none; }
        .rec-ts { font-size: 13px; color: #3b82f6; font-weight: 500; }
        .rec-speaker { font-size: 13px; font-weight: 600; color: var(--text-dark, #1a202c); }
        .rec-text { font-size: 13px; color: var(--text-muted, #718096); line-height: 1.5; }

        /* ── Tablet (≤768px) ── */
        @media (max-width: 768px) {
          .rec-stats { grid-template-columns: repeat(2, 1fr); }
          .rec-detail-grid { grid-template-columns: 1fr; }
          .rec-transcript-row { grid-template-columns: 55px 90px 1fr; }
          .rec-stat-card { padding: 16px; }
          .rec-stat-value { font-size: 22px; }
          .rec-expanded { padding: 14px; }
        }

        /* ── Mobile (≤640px) ── */
        @media (max-width: 640px) {
          .rec-search-row { gap: 8px; }
          .rec-search-box { flex: 1 1 100%; }
          .rec-filter-btn { flex: 1; justify-content: center; }
          .rec-row-header { padding: 12px 14px; gap: 10px; }
          .rec-avatar { width: 38px; height: 38px; font-size: 16px; }
          .rec-meta-title { font-size: 14px; }
          .rec-date-col { display: none; }
        }

        /* ── Small mobile (≤480px) ── */
        @media (max-width: 480px) {
          .rec-stats { grid-template-columns: 1fr; }
          .rec-stat-card { padding: 14px 12px; gap: 12px; }
          .rec-stat-icon { width: 40px; height: 40px; }
          .rec-stat-value { font-size: 20px; }
          .rec-transcript-row { grid-template-columns: 1fr; gap: 4px; }
          .rec-ts { font-size: 12px; }
          .rec-speaker { font-size: 12px; }
          .rec-detail-card { padding: 14px; }
          .rec-video-btns { flex-direction: column; width: 100%; }
          .rec-video-play-btn { width: 100%; justify-content: center; }
          .rec-expanded { padding: 12px; }
        }
      `}</style>

      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Video size={24} />
          Client Recordings
        </h1>
        <p className="page-subtitle">View session recordings with client details and full transcript</p>
      </div>

      {/* Stats */}
      <div className="rec-stats">
        <div className="rec-stat-card">
          <div className="rec-stat-icon" style={{ background: '#f0f4ff' }}>
            <Video size={22} color="#4f6ef7" />
          </div>
          <div>
            <div className="rec-stat-value">{withRecordings.length}</div>
            <div className="rec-stat-label">Total Recordings</div>
          </div>
        </div>
        <div className="rec-stat-card">
          <div className="rec-stat-icon" style={{ background: '#f0fdf4' }}>
            <FileText size={22} color="#22c55e" />
          </div>
          <div>
            <div className="rec-stat-value">{withTranscripts}</div>
            <div className="rec-stat-label">With Transcripts</div>
          </div>
        </div>
        <div className="rec-stat-card">
          <div className="rec-stat-icon" style={{ background: '#fff7ed' }}>
            <Clock size={22} color="#f97316" />
          </div>
          <div>
            <div className="rec-stat-value">{formatTotalDuration(totalDurationSeconds)}</div>
            <div className="rec-stat-label">Total Duration</div>
          </div>
        </div>
      </div>

      {/* Search + filter */}
      <div className="rec-search-row">
        <div className="rec-search-box">
          <Search size={16} color="#718096" />
          <input
            type="text"
            placeholder="Search by client name or session title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          className={`rec-filter-btn ${filter === 'all' ? '' : ''}`}
          style={filter === 'all' ? { background: 'white' } : {}}
          onClick={() => setFilter(filter === 'all' ? 'with_transcript' : 'all')}
        >
          <Filter size={14} />
          {filter === 'all' ? 'All Recordings' : 'With Transcripts'}
          <ChevronDown size={14} />
        </button>
      </div>

      {/* Recording list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'white', border: '1px solid var(--border, #e2e8f0)', borderRadius: 12 }}>
          <Video size={48} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No recordings found</p>
          <p style={{ fontSize: 14 }}>Recordings will appear here after sessions are completed.</p>
        </div>
      ) : (
        filtered.map((rec) => (
          <div className="rec-row" key={rec.id}>
            {/* Row header */}
            <div className="rec-row-header" onClick={() => handleExpand(rec)}>
              <div className="rec-avatar">{getInitial(rec.client_name)}</div>
              <div className="rec-meta">
                <div className="rec-meta-title">{rec.session_title || rec.target_role || 'Mock Interview'}</div>
                <div className="rec-meta-sub">{rec.client_name || 'Client'}</div>
              </div>
              <div className="rec-date-col">
                <div className="rec-date-item"><Calendar size={13} />{formatDate(rec.scheduled_at, interviewerTz)}</div>
                <div className="rec-date-item"><Clock size={13} />{formatDuration(videoDurations[rec.id] ?? rec.duration_seconds)}</div>
              </div>
              <div className="rec-actions">
                <button
                  className="rec-play-btn"
                  onClick={(e) => rec.recording_url ? handlePlay(rec, e) : e.stopPropagation()}
                  disabled={!rec.recording_url}
                  title={rec.recording_url ? 'Play recording' : 'Recording not yet available'}
                  style={{ opacity: rec.recording_url ? 1 : 0.35, cursor: rec.recording_url ? 'pointer' : 'not-allowed' }}
                >
                  {playingId === rec.id ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: 2 }} />}
                </button>
                {expandedId === rec.id ? <ChevronUp size={18} color="#718096" /> : <ChevronDown size={18} color="#718096" />}
              </div>
            </div>

            {/* Expanded detail */}
            {expandedId === rec.id && (
              <div className="rec-expanded">
                <div className="rec-detail-grid">
                  {/* Client Information */}
                  <div className="rec-detail-card">
                    <div className="rec-detail-card-title">
                      <User size={14} /> Client Information
                    </div>
                    <div className="rec-info-row">
                      <span className="rec-info-label"><User size={13} style={{ display: 'inline', marginRight: 4 }} />Name:</span>
                      <span className="rec-info-value">{rec.client_name || '—'}</span>
                    </div>
                    <div className="rec-info-row">
                      <span className="rec-info-label"><Mail size={13} style={{ display: 'inline', marginRight: 4 }} />Email:</span>
                      <span className="rec-info-value">{rec.client_email || '—'}</span>
                    </div>
                    <div className="rec-info-row">
                      <span className="rec-info-label"><Briefcase size={13} style={{ display: 'inline', marginRight: 4 }} />Target Role:</span>
                      <span className="rec-info-value">{rec.target_role || '—'}</span>
                    </div>
                    <div className="rec-info-row">
                      <span className="rec-info-label"><Globe size={13} style={{ display: 'inline', marginRight: 4 }} />Timezone:</span>
                      <span className="rec-info-value">{rec.client_timezone || '—'}</span>
                    </div>
                    <div className="rec-info-row" style={{ marginBottom: 0 }}>
                      <span className="rec-info-label"><Languages size={13} style={{ display: 'inline', marginRight: 4 }} />Language:</span>
                      <span className="rec-info-value">{rec.client_language || '—'}</span>
                    </div>
                  </div>

                  {/* Video Recording */}
                  <div className="rec-detail-card">
                    <div className="rec-detail-card-title">
                      <Video size={14} /> Video Recording
                    </div>
                    <div className="rec-video-area">
                      {rec.recording_url ? (
                        <video
                          ref={expandedId === rec.id ? videoRef : undefined}
                          src={buildRecordingSrc(rec.recording_url) ?? undefined}
                          controls
                          style={{ width: '100%', borderRadius: 8, maxHeight: 200 }}
                          onPlay={() => setPlayingId(rec.id)}
                          onPause={() => setPlayingId(null)}
                          onEnded={() => setPlayingId(null)}
                          onLoadedMetadata={(e) => {
                            const d = Math.round((e.target as HTMLVideoElement).duration)
                            if (d && isFinite(d)) setVideoDurations((prev) => ({ ...prev, [rec.id]: d }))
                          }}
                        />
                      ) : (
                        <div className="rec-video-placeholder">
                          <Video size={40} style={{ opacity: 0.3 }} />
                          <div className="rec-video-filename">
                            Recording will appear here once Zoom finishes processing
                          </div>
                        </div>
                      )}
                      <div className="rec-video-btns">
                        <button
                          className="rec-video-play-btn"
                          style={{ background: rec.recording_url ? '#1e2432' : '#9ca3af', color: 'white', cursor: rec.recording_url ? 'pointer' : 'not-allowed' }}
                          disabled={!rec.recording_url}
                          onClick={(e) => handlePlay(rec, e)}
                        >
                          {playingId === rec.id ? <><Pause size={15} /> Pause</> : <><Play size={15} /> Play</>}
                        </button>
                        <button
                          className="rec-video-play-btn"
                          style={{ background: rec.recording_url ? '#374151' : '#9ca3af', color: 'white', cursor: rec.recording_url ? 'pointer' : 'not-allowed' }}
                          disabled={!rec.recording_url}
                          onClick={(e) => handleDownload(rec, e)}
                        >
                          <Download size={15} /> Download
                        </button>
                      </div>
                      {(videoDurations[rec.id] ?? rec.duration_seconds) && (
                        <p style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#718096', marginTop: 4 }}>
                          <Clock size={13} />
                          Duration: <strong style={{ color: '#1a202c' }}>{formatDuration(videoDurations[rec.id] ?? rec.duration_seconds)}</strong>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Full Transcript — always shown */}
                <div className="rec-transcript">
                  <div className="rec-transcript-title">
                    <FileText size={14} /> Transcript
                  </div>
                  {!rec.transcript_url ? (
                    <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      Transcript will appear here once Zoom finishes processing the recording.
                    </p>
                  ) : transcripts[rec.id] === undefined ? (
                    <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading transcript...</p>
                  ) : transcripts[rec.id].length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No transcript content available.</p>
                  ) : (
                    <div className="rec-transcript-scroll">
                      {transcripts[rec.id].map((line, idx) => (
                        <div className="rec-transcript-row" key={idx}>
                          <span className="rec-ts">{line.timestamp}</span>
                          <span className="rec-speaker">{line.speaker}</span>
                          <span className="rec-text">{line.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  )
}
